import { useState } from 'react';
import { Link } from 'react-router-dom';
import { alertsAPI } from '../services/api';
import { FEELINGS, ALERT_LEVELS } from '../utils/constants.js';
import {
    alertLevelEmoji,
    getAlertMotive,
    groupAlertsByPerson,
} from '../utils/supervisorAlerts.js';
import { copyWhatsAppMessage } from '../utils/whatsappMessage.js';

/** Gestión de alertas en columna 2 — una persona, estado actual, marcar vista o intervenir. */
export default function SupervisorAlertsPanel({
    alerts = [],
    teamData = [],
    onSelectPerson,
    onAlertHandled,
}) {
    const [interventionId, setInterventionId] = useState(null);
    const [actionText, setActionText] = useState('');
    const [busy, setBusy] = useState(false);
    const [seenMsg, setSeenMsg] = useState(null);

    const grouped = groupAlertsByPerson(alerts, teamData);

    async function handleAcknowledge(alertId) {
        setBusy(true);
        setSeenMsg(null);
        try {
            await alertsAPI.acknowledge(alertId);
            setSeenMsg('Quedó registrado que viste y leíste esta alerta.');
            onAlertHandled?.();
        } catch (e) {
            console.error(e);
            setSeenMsg('No se pudo marcar como vista. Intenta de nuevo.');
        } finally {
            setBusy(false);
        }
    }

    async function handleIntervene(alertId) {
        if (!actionText.trim()) return;
        setBusy(true);
        try {
            await alertsAPI.intervene(alertId, { actionTaken: actionText, outcome: null });
            setInterventionId(null);
            setActionText('');
            setSeenMsg('Intervención guardada.');
            onAlertHandled?.();
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
        }
    }

    if (grouped.length === 0) {
        return <p className="sup-team-hint">Nada urgente por ahora. Revisa la lista de personas.</p>;
    }

    return (
        <>
            <p className="sup-team-hint">
                Estado actual de llegada a jornada (último registro). Las alertas son señales activas hoy, no un
                historial por fechas.
            </p>
            {seenMsg && <p className="sup-alert-feedback">{seenMsg}</p>}
            <ul className="sup-alert-list sup-alert-list-grouped">
                {grouped.map((row) => {
                    const mood = FEELINGS[row.estado_hoy];

                    return (
                        <li key={row.employeeId} className={`sup-alert-person sup-alert-person-${row.topLevel}`}>
                            <button
                                type="button"
                                className="sup-alert-person-header-btn"
                                onClick={() => onSelectPerson?.(row.employeeId)}
                            >
                                <span className="sup-alert-name">{row.nombre?.split(' ')[0]}</span>
                                <span className="sup-alert-estado">
                                    {mood ? (
                                        <>
                                            {mood.emoji} Estado actual: <strong>{mood.label}</strong>
                                        </>
                                    ) : (
                                        <>Estado actual: <strong>sin registro reciente</strong></>
                                    )}
                                </span>
                            </button>

                            <ul className="sup-alert-details">
                                {row.alerts.map((alert) => {
                                    const levelInfo = ALERT_LEVELS[alert.level];
                                    return (
                                        <li key={alert.id} className={`sup-alert-detail sup-alert-detail-${alert.level}`}>
                                            <p className="sup-alert-detail-head">
                                                <span aria-hidden>{alertLevelEmoji(alert.level)}</span>
                                                <strong>{alert.title || alert.alert_code}</strong>
                                                {levelInfo && (
                                                    <span className="sup-alert-detail-level">{levelInfo.label}</span>
                                                )}
                                            </p>
                                            <p className="sup-alert-motive">
                                                <span className="sup-alert-motive-label">Por qué se activó:</span>{' '}
                                                {getAlertMotive(alert)}
                                            </p>
                                            {alert.accompaniment?.do_now?.length ? (
                                                <div className="sup-alert-guide">
                                                    <p className="sup-alert-motive-label">Qué puedes hacer ahora:</p>
                                                    <ul>
                                                        {alert.accompaniment.do_now.map((step) => (
                                                            <li key={step}>{step}</li>
                                                        ))}
                                                    </ul>
                                                    {alert.accompaniment.next_step ? (
                                                        <p className="sup-alert-next">
                                                            <strong>Siguiente:</strong> {alert.accompaniment.next_step}
                                                        </p>
                                                    ) : null}
                                                    {alert.accompaniment.avoid?.length ? (
                                                        <p className="sup-alert-avoid text-muted text-sm">
                                                            Mejor evitar: {alert.accompaniment.avoid.join(' · ')}
                                                        </p>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        className="sup-link-btn"
                                                        onClick={async () => {
                                                            const tip =
                                                                alert.accompaniment.do_now[0] ||
                                                                'Quiero acompañarte: ¿cómo vas hoy?';
                                                            await copyWhatsAppMessage(tip, {
                                                                name: row.nombre,
                                                            });
                                                            setSeenMsg('Texto copiado para WhatsApp.');
                                                        }}
                                                    >
                                                        Copiar tip para WhatsApp
                                                    </button>
                                                </div>
                                            ) : null}
                                            <div className="sup-alert-detail-actions">
                                                <button
                                                    type="button"
                                                    className="sup-alert-action secondary"
                                                    disabled={busy}
                                                    onClick={() => handleAcknowledge(alert.id)}
                                                >
                                                    👁 Marcar vista
                                                </button>
                                                <button
                                                    type="button"
                                                    className="sup-alert-action"
                                                    disabled={busy}
                                                    onClick={() => {
                                                        setInterventionId(alert.id);
                                                        setActionText('');
                                                    }}
                                                >
                                                    Gestionar alerta
                                                </button>
                                            </div>
                                            {interventionId === alert.id && (
                                                <div className="sup-alert-intervention">
                                                    <p className="sup-panel-section-label">Registrar intervención</p>
                                                    <textarea
                                                        className="sup-panel-notes"
                                                        rows={2}
                                                        value={actionText}
                                                        onChange={(e) => setActionText(e.target.value)}
                                                        placeholder="Qué hiciste o acordaste con la persona…"
                                                    />
                                                    <div className="sup-alert-intervention-btns">
                                                        <button
                                                            type="button"
                                                            className="sup-link-btn"
                                                            onClick={() => {
                                                                setInterventionId(null);
                                                                setActionText('');
                                                            }}
                                                        >
                                                            Cancelar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="sup-alert-action"
                                                            disabled={busy}
                                                            onClick={() => handleIntervene(alert.id)}
                                                        >
                                                            Guardar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    );
                })}
            </ul>
            <p className="sup-alerts-foot">
                <Link to="/alerts">Abrir bandeja completa de alertas →</Link>
            </p>
        </>
    );
}
